import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { defaultRemarkContent, learningRecordService } from '../services/learningRecordService';
import type { KnowledgeParticleRemarkContent } from '../types/domain';

export function LearningRecordPage() {
  const { particleId = '' } = useParams();
  const { user } = useAuth();
  const [content, setContent] = useState<KnowledgeParticleRemarkContent>(defaultRemarkContent);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      setLoading(true);
      try {
        const result = await learningRecordService.getLearningRecord(particleId, user.id);
        const merged = result.remark?.remark_content || result.defaultContent;
        setContent(merged);
        const others = merged.others as Record<string, string>;
        setNotes(typeof others?.webNotes === 'string' ? others.webNotes : '');
      } finally {
        setLoading(false);
      }
    })();
  }, [particleId, user?.id]);

  async function handleSave() {
    if (!user?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      await learningRecordService.saveLearningRecord({
        particleId,
        userId: user.id,
        remarkContent: {
          ...content,
          others: { ...content.others, webNotes: notes },
        },
      });
      setMessage('已保存');
    } catch (saveError) {
      setMessage(saveError instanceof Error ? saveError.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-muted">加载中…</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link to={`/knowledge/${particleId}`} className="text-sm text-primary">← 返回颗粒</Link>
      <h1 className="text-2xl font-semibold text-ink">私人学习记录</h1>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={12}
        className="w-full rounded-xl border border-border bg-surface p-4 shadow-card"
        placeholder="记录你的理解、卡点与类比…"
      />
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => void handleSave()} disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50">
          {saving ? '保存中…' : '保存'}
        </button>
        {message ? <span className="text-sm text-muted">{message}</span> : null}
      </div>
    </div>
  );
}
